import { Component, EventEmitter, inject, Output } from '@angular/core';
import { finalize, take } from 'rxjs';
import { CurriculumService } from 'src/app/services/curriculum.service';
import { environment } from 'src/environments/environment';
import { FileNameUtils } from 'src/app/shared/utils/FileNameUtils';

@Component({
    selector: 'app-download-cv',
    templateUrl: './download-cv.component.html',
    standalone: false,
})
export class DonwnloadCVComponent {

    @Output() errorEvent = new EventEmitter<string>();

    readonly #curriculumService = inject(CurriculumService);

    loadingStandard = false;
    loadingReduced = false;
    loadingOriginal = false;

    setLoading(mode: 'standard' | 'reduced' | 'original', value: boolean) {
        if (mode === 'standard') {
            this.loadingStandard = value;
        } else if (mode === 'reduced') {
            this.loadingReduced = value;
        } else {
            this.loadingOriginal = value;
        }
    }

    downloadCV(full: boolean): void {
        const setLoading = (value: boolean): void => {
            full ? this.loadingStandard = value : this.loadingReduced = value;
        };

        const documentId = this.#curriculumService.getDocumentId();
        if (!documentId) {
            this.errorEvent.emit('ID de documento no encontrado.');
            return;
        }

        setLoading(true);

        // Fecha actual en formato YYYYMMDD
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}${mm}${dd}`;

        const type = full ? 'standard' : 'reduced';
        const fileName = `${documentId}_${type}_${dateStr}.pdf`;

        this.#curriculumService.downloadCV(documentId, full)
            .pipe(finalize(() => setLoading(false)))
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = FileNameUtils.ensureExtensionFromBlob(fileName, blob, 'pdf'); // nombre con fecha + extensión real
                    link.click();

                    window.URL.revokeObjectURL(url);
                },
                error: err => {
                    console.error('Error descargando CV', err);
                }
            });
    }

    downloadCVOriginal(): void {
        const documentId = this.#curriculumService.getDocumentId();
        if (!documentId) {
            this.errorEvent.emit('ID de documento no encontrado.');
            return;
        }

        this.loadingOriginal = true;

        // Fecha actual en formato YYYYMMDD
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}${mm}${dd}`;

        const fileName = `${documentId}_original_${dateStr}.pdf`;

        this.#curriculumService.downloadCVOriginal(documentId)
            .pipe(finalize(() => this.loadingOriginal = false))
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = url;
                    link.download = FileNameUtils.ensureExtensionFromBlob(fileName, blob, 'pdf'); // nombre con fecha + extensión real
                    link.click();

                    window.URL.revokeObjectURL(url);
                },
                error: err => {
                    console.error('Error descargando CV original', err);
                }
            });
    }

    private handleDownload(base64Pdf: string, filename: string, mode: 'standard' | 'reduced' | 'original'): void {
        this.setLoading(mode, false);

        const byteCharacters = atob(base64Pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }
}
